/**
 * 体积结果可视化插件 —— 将 VolumeService 中的体积结果渲染为 Three.js 叠加层
 *
 * 职责：
 * 1. 作为 RenderContributor 参与 Overlay3D 阶段渲染
 * 2. 根据 VolumeResult 差分创建/销毁 Three.js 可视化对象
 * 3. 渲染拖拽中间状态（预览半透明 box）
 */

import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import { createTextSprite, disposeTextSprite } from '@/plugins/tools/shared/TextSprite'
import {
  createFillMaterial,
  createLineMaterial,
  TOOL_COLOR_VOLUME,
} from '@/plugins/tools/shared/ToolMaterials'
import {
  getActiveViewId,
  getOptionalViewRegistry,
  getViewToolsRoot,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { Plugin } from '@/shared/types/plugin'
import type {
  RenderContributor,
  RenderPipeline,
  ViewRegistry,
  VolumeService,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import type { VolumeResult } from './VolumeData'

// ─── 常量 ───

/** 标签在 group userData 中的存储键 */
const LABEL_KEY = '_volumeLabel'

/** 线框盒子 24 个顶点（12 条边，每条 2 个端点），单位立方体 [-0.5, 0.5] */
const WIREFRAME_POSITIONS = new Float32Array([
  // 底面 4 条边
  -0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, -0.5, 0.5, -0.5, -0.5, -0.5, -0.5,
  -0.5, -0.5, -0.5, -0.5, -0.5, -0.5, 0.5,
  // 顶面 4 条边
  -0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, -0.5, 0.5, -0.5,
  -0.5, 0.5, -0.5, -0.5, 0.5, 0.5,
  // 竖直 4 条边
  -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5, -0.5, 0.5, 0.5, -0.5,
  -0.5, -0.5, -0.5, -0.5, 0.5, -0.5,
])

// ─── 辅助函数 ───

/** 格式化体积标签（保留 2 位小数 + m3 后缀） */
function formatVolume(cubicMeters: number): string {
  return `${cubicMeters.toFixed(2)} m\u00B3`
}

/** 安全释放 Group 内所有子对象的 geometry/material/texture */
function disposeGroup(group: THREE.Group): void {
  group.traverse((obj) => {
    if (obj instanceof THREE.Sprite) {
      disposeTextSprite(obj)
      return
    }
    const meshLike = obj as THREE.Mesh | THREE.Line | THREE.Points
    if (meshLike.geometry) {
      meshLike.geometry.dispose()
    }
    if (meshLike.material) {
      const materials = Array.isArray(meshLike.material) ? meshLike.material : [meshLike.material]
      for (const mat of materials) {
        mat.dispose()
      }
    }
  })
  while (group.children.length > 0) {
    group.remove(group.children[0])
  }
}

/** 创建线框 box（LineSegments） */
function createWireframeBox(): THREE.LineSegments {
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(WIREFRAME_POSITIONS.slice(), 3))
  const material = createLineMaterial(TOOL_COLOR_VOLUME)
  return new THREE.LineSegments(geometry, material)
}

/** 创建半透明填充面 box */
function createFillBox(): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(1, 1, 1)
  const material = createFillMaterial(TOOL_COLOR_VOLUME, 0.3)
  return new THREE.Mesh(geometry, material)
}

/** 根据 VolumeResult 创建完整的可视化 Group */
function createVolumeVisual(result: VolumeResult): THREE.Group {
  const group = new THREE.Group()
  group.name = `volume-${result.id}`

  // 线框 box
  group.add(createWireframeBox())

  // 半透明面
  group.add(createFillBox())

  // 体积标签，放在 box 顶部中心（本地空间 y=+0.5）
  const volumeText =
    result.computedVolume !== null ? formatVolume(result.computedVolume) : '-- m\u00B3'
  const label = createTextSprite(volumeText)
  label.position.set(0, 0.5, 0)
  group.add(label)
  group.userData[LABEL_KEY] = label

  // 通过矩阵应用变换
  group.matrixAutoUpdate = false
  group.matrix.copy(result.matrix)

  return group
}

/** 创建拖拽预览用的 Group（仅线框 + 半透明面，无标签） */
function createDragPreview(matrix: THREE.Matrix4): THREE.Group {
  const group = new THREE.Group()
  group.name = 'volumeDragPreview'

  group.add(createWireframeBox())
  group.add(createFillBox())

  group.matrixAutoUpdate = false
  group.matrix.copy(matrix)

  return group
}

// ─── 插件实现 ───

/** 体积结果可视化插件 */
export class VolumeOverlayPlugin implements Plugin, RenderContributor {
  /** 插件名称 */
  readonly name = 'volume-overlay'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 55
  /** 依赖的服务名列表 */
  readonly dependencies = ['VolumeService', 'RenderPipeline'] as const

  // RenderContributor
  /** 渲染贡献者类型标识 */
  readonly kind = 'volume-overlay'
  /** 需要参与的渲染阶段 */
  readonly phases = [RenderPhase.Overlay3D] as const
  /** 渲染优先级 */
  readonly renderPriority = 0

  /** 引擎上下文 */
  private context!: EngineContext
  /** 体积服务 */
  private volumeService!: VolumeService
  /** 渲染管线 */
  private pipeline!: RenderPipeline
  private viewRegistry: ViewRegistry | null = null

  /** 可视化根节点，挂载到 toolsRoot */
  private readonly root = new THREE.Group()
  /** 临时渲染场景，避免重复渲染 mainScene */
  private readonly overlayScene = new THREE.Scene()
  /** 已渲染的体积结果缓存 */
  private readonly visualCache = new Map<string, THREE.Group>()
  /** 拖拽预览组 */
  private readonly previewGroup = new THREE.Group()
  /** 上一帧是否有拖拽状态，用于判断是否需要更新预览 */
  private hadDragLastFrame = false

  constructor() {
    this.root.name = 'volumeOverlayRoot'
    this.previewGroup.name = 'volumePreview'
    this.root.add(this.previewGroup)
  }

  // ─── Plugin 生命周期 ───

  /**
   * 初始化：保存上下文。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
  }

  /** 启动：获取服务，注册 RenderContributor，挂载根节点 */
  onStart(): void {
    this.volumeService = this.context.getService('VolumeService')
    this.pipeline = this.context.getService('RenderPipeline')
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.pipeline.addRenderContributor(this)
    this.resolveToolsRoot(getActiveViewId(this.viewRegistry)).add(this.root)
  }

  /** 销毁：清理所有 Three.js 对象 */
  onDestroy(): void {
    this.pipeline.removeRenderContributor(this)

    for (const [, group] of this.visualCache) {
      disposeGroup(group)
    }
    this.visualCache.clear()

    disposeGroup(this.previewGroup)
    this.hadDragLastFrame = false

    if (this.root.parent) {
      this.root.parent.remove(this.root)
    }
    this.viewRegistry = null
  }

  // ─── RenderContributor ───

  /**
   * 渲染体积叠加层。
   *
   * @param frame - 帧上下文
   * @param _phase - 当前渲染阶段
   * @param ctx - 当前阶段渲染上下文
   */
  render(frame: ViewFrameContext, _phase: RenderPhase, ctx: PhaseRenderContext): void {
    const activeViewId = getActiveViewId(this.viewRegistry)
    if (activeViewId && ctx.viewId !== activeViewId) {
      return
    }

    const toolsRoot = this.resolveToolsRoot(activeViewId ?? ctx.viewId)
    if (this.root.parent !== toolsRoot) {
      this.root.parent?.remove(this.root)
      toolsRoot.add(this.root)
    }

    // 1. 差分更新已完成的体积结果
    this.syncResultVisuals()

    // 2. 更新拖拽中间状态预览
    this.syncDragPreview()

    // 3. 使用临时场景只渲染自己的 overlay
    if (this.root.children.length === 0) return

    this.overlayScene.children.length = 0
    this.overlayScene.add(this.root)

    submitSceneInView(ctx, this.overlayScene, frame.camera)

    // 渲染后归还 root 到 toolsRoot
    toolsRoot.add(this.root)
  }

  private resolveToolsRoot(viewId: string | null): THREE.Object3D {
    return getViewToolsRoot(this.context, this.viewRegistry, viewId)
  }

  // ─── 私有方法 ───

  /** 差分同步 VolumeService.results → 可视化缓存 */
  private syncResultVisuals(): void {
    const results = this.volumeService.getResults()
    const currentIds = new Set<string>()

    for (const result of results) {
      currentIds.add(result.id)
      if (!this.visualCache.has(result.id)) {
        // 新增结果 → 创建可视化
        const visual = createVolumeVisual(result)
        this.visualCache.set(result.id, visual)
        this.root.add(visual)
      }
    }

    // 删除已移除的结果
    for (const [id, group] of this.visualCache) {
      if (!currentIds.has(id)) {
        disposeGroup(group)
        this.root.remove(group)
        this.visualCache.delete(id)
      }
    }
  }

  /** 同步拖拽中间状态预览 */
  private syncDragPreview(): void {
    const drag = this.volumeService.getCurrentDrag()

    if (!drag) {
      // 无拖拽状态，如果上一帧有则清理
      if (this.hadDragLastFrame) {
        disposeGroup(this.previewGroup)
        this.hadDragLastFrame = false
      }
      return
    }

    // 有拖拽状态，每帧重建预览（拖拽过程中矩阵持续变化）
    disposeGroup(this.previewGroup)
    const preview = createDragPreview(drag.matrix)
    // 将预览子对象移入 previewGroup
    while (preview.children.length > 0) {
      const child = preview.children[0]
      preview.remove(child)
      this.previewGroup.add(child)
    }
    this.previewGroup.matrixAutoUpdate = false
    this.previewGroup.matrix.copy(drag.matrix)
    this.hadDragLastFrame = true
  }
}

export default VolumeOverlayPlugin
